import { IFullStop } from 'types'
import { LOAD_STATE } from '../constants/loads'
import { IStop } from '../types/loads'

// Returns a number based off the shipment status and stopSequence being passed in, which is used to compare with a stops stopSequence number.
// Logic only accounts for statuse updates to At Pickup, At Drop Off, In Transit or Delivered.
export const getSequenceOrder = (
  stops: IStop[] | IFullStop[],
  statusUpdate: LOAD_STATE,
  sequenceUpdate: string
): number => {
  const sequenceUpdateNumber = parseInt(sequenceUpdate, 10)
  switch (statusUpdate) {
    // stopSequence is incremented by 1 when we update a status to In Transit.
    // deducting 1 from the sequenceUpdate number gives us the stopSequence for the current stop
    case LOAD_STATE['In Transit']:
      return sequenceUpdateNumber - 1
    // stopSequence is null when we update a status to Delivered.
    // stops.length will give the stopSequence for the last stop
    case LOAD_STATE.Delivered:
      return stops.length
    // stopSequence remains the same when we update to At Pickup or At Dropoff
    default:
      return sequenceUpdateNumber
  }
}

// Used for checking shipment status updates.  Takes a statusUpdate value, and stops stopSequence number and determines if In or Out times should visible/present per stop.
export const getAreTimesVisible = (
  statusUpdate: LOAD_STATE,
  sequenceOrderNumber: number,
  stopSequenceNumber: number = 0
) => {
  const isCheckinVisible = [
    sequenceOrderNumber >= stopSequenceNumber,
    statusUpdate === LOAD_STATE['At Pickup'],
    statusUpdate === LOAD_STATE['At Dropoff'],
  ].includes(true)
  const isCheckoutVisible = [
    sequenceOrderNumber > stopSequenceNumber,
    statusUpdate === LOAD_STATE['In Transit'],
    statusUpdate === LOAD_STATE.Delivered,
  ].includes(true)
  const isStopVisible = sequenceOrderNumber >= stopSequenceNumber
  return {
    isCheckinVisible: isStopVisible ? isCheckinVisible : false,
    isCheckoutVisible: isStopVisible ? isCheckoutVisible : false,
    isStopVisible,
  }
}

// Depending on the shipment status, return only the stops that require a checkin/checkout time in order to submit a status update.
const filterNonRequiredStops = (
  stops: IStop[],
  statusUpdate: LOAD_STATE,
  newStopSequence: string
): IStop[] => {
  const sequenceOrderNumber = getSequenceOrder(stops, statusUpdate, newStopSequence)
  return stops.filter(s => {
    const { isStopVisible } = getAreTimesVisible(statusUpdate, sequenceOrderNumber, s.stopSequence)
    return isStopVisible
  })
}

// Checks the stops that require an in or out time, and confirm that in or out times exist for those stops.
export const areRequiredStopsValid = (
  stops: IStop[],
  statusUpdate: LOAD_STATE,
  newStopSequence: string
): boolean => {
  const requiredStops = filterNonRequiredStops(stops, statusUpdate, newStopSequence)
  const validateRequiredStops = requiredStops.map(s => {
    const sequenceOrderNumber = getSequenceOrder(stops, statusUpdate, newStopSequence)
    const { isCheckinVisible, isCheckoutVisible } = getAreTimesVisible(
      statusUpdate,
      sequenceOrderNumber,
      s.stopSequence
    )
    return {
      ...(isCheckinVisible && { checkIn: s.checkedInAt }),
      ...(isCheckoutVisible && { checkOut: s.checkedOutAt }),
    }
  })

  return validateRequiredStops.every(item => {
    return Object.values(item).every(val => !!val)
  })
}

// Depending on the shipment status, return only the stops that require a checkin/checkout time in order to submit a status update.
const filterNonRequiredMpStops = (
  stops: IFullStop[],
  statusUpdate: LOAD_STATE,
  newStopSequence: string
): IFullStop[] => {
  const sequenceOrderNumber = getSequenceOrder(stops, statusUpdate, newStopSequence)
  return stops.filter((s, i) => {
    // FP's stops stopSequences are consecutively numbered.  Marketplaces stopSequences aren't, so we use the index as the stopSequence number arg.
    const { isStopVisible } = getAreTimesVisible(statusUpdate, sequenceOrderNumber, i + 1)
    return isStopVisible
  })
}

// Checks the stops that require an in or out time, and confirm that in or out times exist for those stops.
export const areRequiredMpStopsValid = (
  stops: IFullStop[],
  statusUpdate: LOAD_STATE,
  newStopSequence: string
): boolean => {
  const requiredStops = filterNonRequiredMpStops(stops, statusUpdate, newStopSequence)
  const validateRequiredStops = requiredStops.map((s, i) => {
    const sequenceOrderNumber = getSequenceOrder(stops, statusUpdate, newStopSequence)
    const { isCheckinVisible, isCheckoutVisible } = getAreTimesVisible(
      statusUpdate,
      sequenceOrderNumber,
      i + 1
    )
    const { status } = s || {}
    const { arrivedAt, leftAt } = status || {}
    return {
      ...(isCheckinVisible && { checkIn: arrivedAt }),
      ...(isCheckoutVisible && { checkOut: leftAt }),
    }
  })
  return validateRequiredStops.every(item => {
    return Object.values(item).every(val => !!val)
  })
}
